import { useMemo } from "react"
import { getCurrentTemplateMetrics, getInitialDuplicateSelectedMetrics } from "components/custom-metrics-selector/helpers/template-metrics"
import { isLoading, ERROR, DataStatus } from "helpers/data-statuses"
import GraphErrorFallback from "renderers/multi-line-graph/components/graph-error-fallback"
import { ModelMetadata } from "resources/models"
import type { TemplateResponse } from "resources/templates"
import { useTemplateMetadata } from "resources/templates"
import { useGraphData } from "../../hooks/use-graph-data"
import CustomGraphComponent from "./component"
import CustomGraphMetricsSelector from "./metrics-selector"
import { decodeFamilyMetricIds, resolveCustomGraphFamilyContext, type CustomGraphFamilyContext } from "./family"

// Stable module-level fallback — never a fresh `?? []` literal, which would re-render every consumer.
const EMPTY_ARRAY: string[] = []

interface CustomGraphWrapperProps {
  modelMetadata: ModelMetadata
  templateMetadata?: TemplateResponse
  templateMetadataStatus?: DataStatus
}

export default function CustomGraphWrapper({ modelMetadata, templateMetadata, templateMetadataStatus }: CustomGraphWrapperProps) {
  const { duplicateFromViewName, duplicateFromTemplateId, duplicateFromViewMetrics, duplicateReturnViewId, modelId } = useGraphData()
  const [duplicateSourceMetadata, duplicateSourceMetadataStatus] = useTemplateMetadata(duplicateFromTemplateId ?? undefined)

  const decodedTemplateMetrics = useMemo(() => decodeFamilyMetricIds(templateMetadata?.config?.metrics), [templateMetadata])
  const decodedDuplicateSourceMetrics = useMemo(() => decodeFamilyMetricIds(duplicateSourceMetadata?.config?.metrics), [duplicateSourceMetadata])

  const familyContext: CustomGraphFamilyContext | null = useMemo(() => resolveCustomGraphFamilyContext({
    decodedTemplateMetrics,
    duplicateFromTemplateId,
    decodedDuplicateSourceMetrics,
    duplicateReturnViewId,
  }), [decodedTemplateMetrics, duplicateFromTemplateId, decodedDuplicateSourceMetrics, duplicateReturnViewId])

  const initialSelectedMetrics = useMemo(() => getInitialDuplicateSelectedMetrics({
    duplicateFromTemplateId,
    duplicateFromViewMetrics,
    duplicateSourceMetrics: decodedDuplicateSourceMetrics.ids,
    modelId,
  }), [duplicateFromTemplateId, duplicateFromViewMetrics, decodedDuplicateSourceMetrics, modelId])

  const selectedMetrics = useMemo(() => {
    if (templateMetadata?.config?.metrics && modelId) {
      return getCurrentTemplateMetrics(decodedTemplateMetrics.ids, modelId)
    }
    return initialSelectedMetrics ?? EMPTY_ARRAY
  }, [templateMetadata, decodedTemplateMetrics, modelId, initialSelectedMetrics])

  const decodedTemplateMetadata = useMemo(() => {
    if (!templateMetadata) return templateMetadata
    return { ...templateMetadata, config: { ...templateMetadata.config, metrics: decodedTemplateMetrics.ids } }
  }, [templateMetadata, decodedTemplateMetrics])

  if (templateMetadataStatus != null && (isLoading(templateMetadataStatus) || templateMetadataStatus === ERROR)) {
    return null
  }

  if (duplicateFromTemplateId && isLoading(duplicateSourceMetadataStatus)) {
    return null
  }

  if (!familyContext) {
    return <GraphErrorFallback
      error={new Error("Couldn't load this view's metrics.")}
      resetError={() => window.location.reload()}
    />
  }

  return <>
    <CustomGraphMetricsSelector
      modelMetadata={modelMetadata}
      familyContext={familyContext}
      templateMetadata={decodedTemplateMetadata}
      initialViewName={duplicateFromViewName}
      initialSelectedMetrics={initialSelectedMetrics}
      duplicateSourceTemplateId={duplicateFromTemplateId}
    />
    <CustomGraphComponent modelMetadata={modelMetadata} familyContext={familyContext} selectedMetrics={selectedMetrics} />
  </>
}
